import type { CSSProperties, ReactNode } from 'react';

interface Props {
  value: number | string | null | undefined;
  /** Currency prefix; defaults to PKR. Set to '' to suppress. */
  currency?: string;
  /** Decimal places (default 0 for PKR — no paisas in normal display). */
  decimals?: number;
  /** Strong weight for prominent figures (receipts, KPI). */
  strong?: boolean;
  /** Color the value red when negative (default true). */
  signColor?: boolean;
  /** Render a fallback when value is null/undefined/blank/non-numeric. */
  fallback?: ReactNode;
  style?: CSSProperties;
}

/**
 * Single source of truth for money rendering. Replaces ad-hoc
 * `toLocaleString('en-PK')` calls across modules so the format (currency
 * prefix, grouping, decimals, negative sign) stays consistent.
 *
 * For amounts that should be hidden behind a reveal toggle, wrap this in
 * `<MaskedAmount value={<MoneyText value={…} />}/>` — they compose.
 */
export function MoneyText({
  value,
  currency = 'PKR',
  decimals = 0,
  strong = false,
  signColor = true,
  fallback = '—',
  style,
}: Props) {
  if (value == null || value === '') return <>{fallback}</>;
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return <>{fallback}</>;

  const formatted = new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);

  const negative = num < 0;
  return (
    <span
      style={{
        fontFeatureSettings: '"tnum"',
        fontWeight: strong ? 600 : undefined,
        color: signColor && negative ? 'var(--ant-color-error)' : undefined,
        ...style,
      }}
    >
      {currency && <span style={{ marginInlineEnd: 4, opacity: 0.7 }}>{currency}</span>}
      {formatted}
    </span>
  );
}
